import { useEffect, useState, useMemo, useContext } from "react";
import { ConfirmationDialog, RenderName, TableHeader, TableRow } from "../../components";
import { headersType } from "../../models";
import { useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store";
import { formatCurrency } from "../../utils";
import { getMonthlySubscriptions, getOfficeAnnualPlans, getYearlySubscriptions, updateOfficePlan } from "../../redux/slices/annualPlanSlice";
import { useSelector, useDispatch } from "react-redux";
import { SubscriptionType, PlanNames } from "../../models/annual-plan.model";
import { annualPlansSampleData, customOrder } from "./planData";
import { addPlan, removePlan, clearPlans } from "../../redux/slices/selectedPlanSlice";
import { stripHtml } from "../../utils/stripHtml";
import { toCamelCaseFromPascal } from "../../components/forms/AnnualPlan/Step4";
import { PLAN_FORM_DATA, PlanFormDataType } from "../../utils/planConstant";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";

export const headers: headersType[] = [
  {
    title: "",
    extraClasses: "w-16",
    sortKey: "",
    isClickable: false,
  },
  {
    title: "Plan Name",
    extraClasses: "w-full pl-0 break-all",
    sortKey: "planName",
    isClickable: false,
  },
  {
    title: "Monthly",
    extraClasses: "w-120 hidden lg:block",
    sortKey: "frequency",
    isClickable: false,
  },
  {
    title: "Yearly",
    extraClasses: "w-120 hidden lg:block",
    sortKey: "rate",
    isClickable: false,
  },
  {
    title: "# of Subscribers",
    extraClasses: "w-140 hidden lg:block",
    sortKey: "numUsers",
    isClickable: false,
  },
];

const Plans = () => {
  const { id } = useParams();
  const toast = useContext(ToastContext);
  const dispatch = useDispatch<AppDispatch>();
  const [extraClasses] = useState<string[]>(headers.map(head => head.extraClasses));
  const { annualPlans } = useSelector((state: RootState) => state.annualPlan);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [pendingPlanAction, setPendingPlanAction] = useState<{ plan: any; isChecked: boolean } | null>(null);
  const [formData, setFormData] = useState<PlanFormDataType[]>(PLAN_FORM_DATA);
  const { currentOffice } = useSelector((state: RootState) => state.office);
  const selectedAnnualPlans = useSelector((state: RootState) => state.selectedPlans.selectedPlans);
  const validPlanNames = selectedAnnualPlans.map(plan => plan.planName);
  const selectedPlans = formData.reduce((acc: any, curr) => {
    const shouldInvalidate = curr.planName && !validPlanNames.includes(curr.planName);
    acc[curr.id] = shouldInvalidate ? "" : curr.value;
    return acc;
  }, {});

  useEffect(() => {
    if (annualPlans?.length) {
      const selectedPlans = annualPlans.map(item => {
        return {
          id: `${toCamelCaseFromPascal(item.annualPlanType.name.split(" ").join(""))}Plan${
            item.subscription.subscriptionType === "monthly" ? "Monthly" : "Yearly"
          }SubscriptionId`,
          subscriptionId: item.subscription.subscriptionId,
        };
      });
      const updatedFormData = formData.map(item => {
        const selectedPlan = selectedPlans.find(plan => plan.id === item.id);
        if (selectedPlan) {
          return { ...item, value: selectedPlan.subscriptionId, valid: true };
        }
        return item;
      });
      setFormData(updatedFormData);
    }
    return () => {
      // initialized = false;
    };
  }, [annualPlans]);

  // const finalPlans = annualPlans?.length ? annualPlans : annualPlansSampleData;
  const unmatchedSamplePlans = useMemo(() => {
    return annualPlansSampleData.filter(samplePlan => {
      return !annualPlans.some(
        plan =>
          plan.annualPlanType.name === samplePlan.annualPlanType.name &&
          plan.annualPlanType.subscriptionType === samplePlan.annualPlanType.subscriptionType
      );
    });
  }, [annualPlans, annualPlansSampleData]);

  const finalPlans = useMemo(() => {
    return annualPlans.length ? [...annualPlans, ...unmatchedSamplePlans] : annualPlansSampleData;
  }, [annualPlans, unmatchedSamplePlans]);

  useEffect(() => {
    if (id) {
      dispatch(getOfficeAnnualPlans({ officeId: id }));
      dispatch(getMonthlySubscriptions({ officeId: id }));
      dispatch(getYearlySubscriptions({ officeId: id }));
    }
  }, [dispatch, id]);

  const [result, setResult] = useState<any[]>([]);
  useEffect(() => {
    const processed = finalPlans.reduce((acc: any, curr) => {
      const planName = PlanNames[curr.annualPlanType.name as keyof typeof PlanNames];
      let existingOffice = acc.find((item: any) => item.officeId === curr.officeId && item.planName === planName);
      if (!existingOffice) {
        existingOffice = {
          officeId: curr.officeId,
          planName: planName,
          numSubscribers: curr.numSubscribers,
          monthly: null,
          yearly: null,
          checked: !!curr?.isActive,
          IsActive: !!curr?.isActive,
        };
        acc.push(existingOffice);
      } else {
        existingOffice.numSubscribers += curr.numSubscribers;
        // Update IsActive based on any active subscription for this plan
        if (curr?.isActive) {
          existingOffice.IsActive = true;
        }
        // Update checked status based on IsActive
        existingOffice.checked = existingOffice.IsActive;
      }

      if (curr.annualPlanType.subscriptionType === SubscriptionType.Monthly) {
        existingOffice.monthly = {
          annualPlanType: curr.annualPlanType,
          subscription: curr.subscription,
        };
      } else if (curr.annualPlanType.subscriptionType === SubscriptionType.Yearly) {
        existingOffice.yearly = {
          annualPlanType: curr.annualPlanType,
          subscription: curr.subscription,
        };
      }

      return acc;
    }, []);

    processed.sort((a: any, b: any) => customOrder.indexOf(a.planName) - customOrder.indexOf(b.planName));
    setResult(processed);

    const initiallyCheckedPlans = processed.filter((plan: any) => plan.checked);
    dispatch(clearPlans());
    initiallyCheckedPlans.forEach((plan: any) => dispatch(addPlan(plan)));
  }, [finalPlans]);

  result.sort((a: any, b: any) => customOrder.indexOf(a.planName) - customOrder.indexOf(b.planName));

  const handleCheckboxChange = (plan: any, isChecked: boolean) => {
    const updated = result.map(item => {
      if (item.planName?.toLowerCase() === plan.planName?.toLowerCase() && item.officeId === plan.officeId) {
        return { ...item, checked: isChecked };
      }
      return item;
    });
    setResult(updated);
    // Get the updated plan from the updated array
    const updatedPlan = updated.find(item => item.planName === plan.planName && item.officeId === plan.officeId);
    // Check if we need to show confirmation dialog
    if (updatedPlan && !isChecked && updatedPlan.IsActive === true) {
      setPendingPlanAction({ plan, isChecked });
      setShowConfirmationDialog(true);
      return; // Don't proceed with the action yet
    }

    if (isChecked) {
      dispatch(addPlan(plan));
    } else {
      dispatch(removePlan(plan));
    }
  };

  const handleConfirmationFailure = () => {
    // Revert the checkbox state
    if (pendingPlanAction) {
      const updated = result.map(item => {
        if (item.planName?.toLowerCase() === pendingPlanAction.plan.planName?.toLowerCase() && item.officeId === pendingPlanAction.plan.officeId) {
          return { ...item, checked: !pendingPlanAction.isChecked };
        }
        return item;
      });
      setResult(updated);
    }
    setShowConfirmationDialog(false);
    setPendingPlanAction(null);
  };

  const getSubscriptionKeys = (filteredPlans: any[]) => {
    return filteredPlans.flatMap(item => {
      const monthlyKey = `${toCamelCaseFromPascal(item?.monthly?.annualPlanType.name.split(" ").join(""))}Plan${
        item?.monthly?.annualPlanType.subscriptionType === "monthly" ? "Monthly" : "Yearly"
      }SubscriptionId`;

      const yearlyKey = `${toCamelCaseFromPascal(item?.yearly?.annualPlanType.name.split(" ").join(""))}Plan${
        item?.yearly?.annualPlanType.subscriptionType === "monthly" ? "Monthly" : "Yearly"
      }SubscriptionId`;

      return [monthlyKey, yearlyKey];
    });
  };

  const handleConfirmationSuccess = async () => {
    if (currentOffice?.annualPlanDescription && currentOffice?.officeId) {
      const values = getSubscriptionKeys([pendingPlanAction?.plan]);
      const updatedPlans = { ...selectedPlans };

      values.forEach(key => {
        if (key in updatedPlans) {
          updatedPlans[key] = "";
        }
      });
      const filteredPlans = result.filter(item => item.checked === true && item.IsActive === false);
      const keysArray = getSubscriptionKeys(filteredPlans);
      keysArray.forEach(key => {
        if (key in updatedPlans) {
          updatedPlans[key] = "";
        }
      });
      const notification = stripHtml(currentOffice?.annualPlanDescription);
      const response = await dispatch(
        updateOfficePlan({
          officeId: currentOffice?.officeId,
          body: {
            annualPlanActive: true,
            annualPlanDescription: notification,
            ...updatedPlans,
          },
        })
      );
      if (response) {
        toast?.openToast(`Your Plan has been successfully deactivated.`);
        await dispatch(
          getOfficeAnnualPlans({
            officeId: currentOffice?.officeId,
          })
        );
        setShowConfirmationDialog(false);
      }
    }
  };

  return (
    <div className="w-full relative">
      <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
        {headers.map((head, index) => (
          <TableHeader
            title={head.title}
            index={index}
            key={index}
            sortIcon={null}
            handleSort={() => null}
            extraClassesHeader={head.extraClasses}
            isClickable={head.isClickable}
          />
        ))}
      </div>
      {result?.map((rowData: any, index: number) => {
        // const isChecked = selectedPlans.some(p => p.planName === rowData.planName && p.officeId === rowData.officeId);
        const isChecked = !!rowData?.checked;
        const sortedData = {
          checkbox: (
            <input type="checkbox" checked={isChecked} onChange={e => handleCheckboxChange(rowData, e.target.checked)} className="cursor-pointer" />
          ),
          name: <RenderName extraClasses="capitalize" title={`${rowData.planName}`} subTitle={""} />,
          monthy: `${formatCurrency((rowData.monthly?.subscription?.price / 100).toString(), "blur")}`,
          yearly: `${formatCurrency((rowData.yearly?.subscription?.price / 100).toString(), "blur")}`,
          numUsers: rowData.numSubscribers,
        };
        return (
          <TableRow
            data={Object.values(sortedData)}
            key={index}
            id={rowData.planName}
            tooltipVisible={false}
            extraClassesRow={extraClasses}
            handleOpenTooltip={() => null}
            handleCloseTooltip={() => null}
            tooltipOptions={[]}
            onRowClick={() => null}
          />
        );
      })}
      <ConfirmationDialog
        open={showConfirmationDialog}
        description={"Patients might be enrolled on the plan. Are you sure you want to deactivate the plan ?"}
        successButtonText={"Yes"}
        failureButtonText={"No"}
        handleSuccess={handleConfirmationSuccess}
        handleFailure={handleConfirmationFailure}
      />
    </div>
  );
};

export default Plans;
